import React, { useEffect, useMemo, useRef, useState } from "react";
import { addDoc, collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { FaAd, FaBullhorn, FaCloudUploadAlt, FaSearch, FaToggleOff, FaToggleOn } from "react-icons/fa";
import PermissionGuard from "../components/PermissionGuard";
import { db, storage } from "../firebase";

const PAGES = [
  ["home_feed", "Home Feed"],
  ["explore_feed", "Explore Feed"],
  ["event_list", "Event List"],
  ["event_detail", "Event Detail"],
  ["stories", "Stories"],
  ["posts", "Posts"],
  ["artist_profile", "Artist Profile"],
  ["mentor_profile", "Mentor Profile"],
];
const SLOTS = [
  ["top_banner", "Top Banner"],
  ["inline_card", "Inline Card"],
  ["story_break", "Story Break"],
  ["post_detail_top", "Post Detail Top"],
  ["fullscreen_interstitial", "Fullscreen Interstitial"],
];
const SCOPES = [
  ["all_app", "Run Across All App"],
  ["selected_pages", "Run on Selected Pages"],
  ["specific_content", "Run on Specific Content"],
];
const CONTENT_TYPES = [["post", "Post"], ["story", "Story"], ["event", "Event"], ["artist", "Artist"], ["mentor", "Mentor"]];
const STATUSES = ["hold", "live", "paused", "completed"];

const DEFAULT_FORM = {
  title: "",
  objective: "",
  description: "",
  targetAudience: "all_users",
  targetLocation: "India",
  budget: "",
  startDate: "",
  endDate: "",
  ctaText: "Install Now",
  ctaUrl: "",
  deliveryScope: "all_app",
  selectedPages: ["home_feed", "explore_feed"],
  selectedSlots: ["inline_card"],
  specificContentType: "post",
  specificContentId: "",
  priority: "50",
  frequencyCapPerUserDay: "6",
  videoDurationSec: "20",
};

export default function AppAdds() {
  const [tab, setTab] = useState("create");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [mediaFile, setMediaFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [publishError, setPublishError] = useState("");
  const [publishMessage, setPublishMessage] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [ads, setAds] = useState([]);
  const [loadingAds, setLoadingAds] = useState(true);
  const [search, setSearch] = useState("");
  const [updatingAdId, setUpdatingAdId] = useState("");
  const syncedLiveAdsRef = useRef(new Set());

  useEffect(() => {
    const qRef = query(collection(db, "new_add"), orderBy("createdAt", "desc"), limit(500));
    return onSnapshot(qRef, (snap) => {
      setAds(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      setLoadingAds(false);
    }, () => {
      setAds([]);
      setLoadingAds(false);
    });
  }, []);

  useEffect(() => {
    ads.forEach((ad) => {
      if (String(ad.status || "").toLowerCase() !== "live") return;
      if (syncedLiveAdsRef.current.has(ad.id)) return;
      if (ad.isActive === true && ad.active === true && ad.isLive === true && ad.creativeUrl) return;
      syncedLiveAdsRef.current.add(ad.id);
      updateDoc(doc(db, "new_add", ad.id), {
        ...buildAdCompatibilityPatch(ad, "live"),
        updatedAt: serverTimestamp(),
      }).catch(() => {
        syncedLiveAdsRef.current.delete(ad.id);
      });
    });
  }, [ads]);

  useEffect(() => {
    if (!mediaFile) return setPreviewUrl("");
    const url = URL.createObjectURL(mediaFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [mediaFile]);

  const mediaType = inferMediaType(mediaFile);
  const runtimeHint = mediaType === "video" ? "Video runtime: 20 to 120 sec." : "Image runtime is fixed to 10 sec.";

  const filteredAds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ads;
    return ads.filter((ad) => {
      const hay = [ad.title, ad.status, ad.deliveryScope, ad.specificContentId, (ad.selectedPages || []).join(" "), (ad.selectedSlots || []).join(" ")]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [ads, search]);

  function toggleList(field, value) {
    setForm((prev) => {
      const list = Array.isArray(prev[field]) ? prev[field] : [];
      return { ...prev, [field]: list.includes(value) ? list.filter((x) => x !== value) : [...list, value] };
    });
  }

  async function publishAd(e) {
    e.preventDefault();
    setPublishError("");
    setPublishMessage("");

    const validationError = validate(form, mediaFile);
    if (validationError) return setPublishError(validationError);

    try {
      setIsPublishing(true);
      setUploadProgress(0);
      const uploaded = await uploadMedia(mediaFile, setUploadProgress);

      const durationSec = uploaded.mediaType === "video" ? clampVideoDuration(form.videoDurationSec) : 10;
      const selectedPages = form.deliveryScope === "all_app" ? PAGES.map(([v]) => v) : form.selectedPages;
      const specific = form.deliveryScope === "specific_content" ? {
        type: form.specificContentType,
        id: form.specificContentId.trim(),
      } : null;
      const baseAdPayload = {
        title: form.title.trim(),
        objective: form.objective.trim(),
        description: form.description.trim(),
        targetAudience: form.targetAudience,
        targetLocation: form.targetLocation.trim(),
        budget: Number(form.budget || 0),
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        ctaText: form.ctaText.trim(),
        ctaUrl: form.ctaUrl.trim(),
        mediaUrl: uploaded.mediaUrl,
        mediaType: uploaded.mediaType,
        mediaPath: uploaded.mediaPath,

        deliveryScope: form.deliveryScope,
        selectedPages,
        selectedSlots: form.selectedSlots,
        specificContentType: specific?.type || "",
        specificContentId: specific?.id || "",

        displayDurationSec: durationSec,
        imageDurationSec: 10,
        videoMinDurationSec: 20,
        videoMaxDurationSec: 120,
        priority: Number(form.priority || 50),
        frequencyCapPerUserDay: Number(form.frequencyCapPerUserDay || 6),

        adRuntime: {
          deliveryScope: form.deliveryScope,
          pages: selectedPages,
          slots: form.selectedSlots,
          specificContent: specific,
          displayDurationSec: durationSec,
          imageDurationSec: 10,
          videoMinDurationSec: 20,
          videoMaxDurationSec: 120,
          priority: Number(form.priority || 50),
          frequencyCapPerUserDay: Number(form.frequencyCapPerUserDay || 6),
        },
      };

      const admin = safeEmployee();
      await addDoc(collection(db, "new_add"), {
        ...baseAdPayload,
        ...buildAdCompatibilityPatch(baseAdPayload, "hold"),
        status: "hold",
        impressions: 0,
        clicks: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: {
          id: admin.docId || "",
          name: admin.name || admin.fullName || "",
          role: admin.role || "",
        },
      });

      setPublishMessage("Ad saved in hold status. Set to live from Manage Ads.");
      setForm(DEFAULT_FORM);
      setMediaFile(null);
      setUploadProgress(0);
      setTab("manage");
    } catch (err) {
      setPublishError(err?.message || "Publish failed");
    } finally {
      setIsPublishing(false);
    }
  }

  async function updateStatus(id, status) {
    try {
      setUpdatingAdId(id);
      const current = ads.find((ad) => ad.id === id) || {};
      await updateDoc(doc(db, "new_add", id), {
        status,
        ...buildAdCompatibilityPatch(current, status),
        liveAt: status === "live" ? serverTimestamp() : current.liveAt || null,
        updatedAt: serverTimestamp(),
      });
    } finally {
      setUpdatingAdId("");
    }
  }

  return (
    <PermissionGuard requiredPermission="view_app_adds">
      <div className="aa-pro-page" style={s.page}>
        <style>{css}</style>
        <div className="aa-pro-hero" style={s.hero}>
          <div style={s.heroTitle}><div className="aa-pro-hero-icon"><FaAd /></div><div><h2 className="aa-pro-title">Ads Studio</h2><p className="aa-pro-subtitle" style={{ marginTop: 6 }}>Control where ads run, how long, and who sees them.</p></div></div>
          <div className="aa-pro-segment" style={{ display: "flex", gap: 8 }}>
            <button type="button" style={{ ...s.tab, ...(tab === "create" ? s.tabActive : null) }} onClick={() => setTab("create")}><FaBullhorn /> Create Ads</button>
            <button type="button" style={{ ...s.tab, ...(tab === "manage" ? s.tabActive : null) }} onClick={() => setTab("manage")}><FaCloudUploadAlt /> Manage Ads</button>
          </div>
        </div>

        {tab === "create" ? (
          <form className="aa-pro-table-shell" style={s.panel} onSubmit={publishAd}>
            <div className="grid2">
              <Field label="Ad Title *"><input style={s.input} value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} /></Field>
              <Field label="Objective *"><input style={s.input} value={form.objective} onChange={(e) => setForm((p) => ({ ...p, objective: e.target.value }))} /></Field>
              <Field label="Target Audience"><select style={s.input} value={form.targetAudience} onChange={(e) => setForm((p) => ({ ...p, targetAudience: e.target.value }))}><option value="all_users">All Users</option><option value="artists">Artists</option><option value="mentors">Mentors</option><option value="users">Users</option></select></Field>
              <Field label="Geo Location"><input style={s.input} value={form.targetLocation} onChange={(e) => setForm((p) => ({ ...p, targetLocation: e.target.value }))} /></Field>
              <Field label="Budget"><input style={s.input} type="number" min="0" value={form.budget} onChange={(e) => setForm((p) => ({ ...p, budget: e.target.value }))} /></Field>
              <Field label="Priority (0-100)"><input style={s.input} type="number" min="0" value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))} /></Field>
              <Field label="Frequency Cap / user / day"><input style={s.input} type="number" min="1" value={form.frequencyCapPerUserDay} onChange={(e) => setForm((p) => ({ ...p, frequencyCapPerUserDay: e.target.value }))} /></Field>
              <Field label="CTA URL *"><input style={s.input} value={form.ctaUrl} onChange={(e) => setForm((p) => ({ ...p, ctaUrl: e.target.value }))} /></Field>
              <Field label="Start Date"><input style={s.input} type="date" value={form.startDate} onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))} /></Field>
              <Field label="End Date"><input style={s.input} type="date" value={form.endDate} onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))} /></Field>
            </div>

            <Field label="Delivery Scope">
              <select style={s.input} value={form.deliveryScope} onChange={(e) => setForm((p) => ({ ...p, deliveryScope: e.target.value }))}>
                {SCOPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>

            {form.deliveryScope === "specific_content" ? (
              <div className="grid2">
                <Field label="Content Type"><select style={s.input} value={form.specificContentType} onChange={(e) => setForm((p) => ({ ...p, specificContentType: e.target.value }))}>{CONTENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                <Field label="Content ID *"><input style={s.input} value={form.specificContentId} onChange={(e) => setForm((p) => ({ ...p, specificContentId: e.target.value }))} placeholder="post_abc123" /></Field>
              </div>
            ) : null}

            {form.deliveryScope !== "all_app" ? (
              <Choice title="Pages *" options={PAGES} selected={form.selectedPages} toggle={(v) => toggleList("selectedPages", v)} />
            ) : <div style={s.note}>All app pages selected by scope.</div>}

            <Choice title="In-App Slots *" options={SLOTS} selected={form.selectedSlots} toggle={(v) => toggleList("selectedSlots", v)} />

            <Field label="Description"><textarea style={s.textarea} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} /></Field>
            <Field label="Ad Media (image/video) *"><input style={s.input} type="file" accept="image/*,video/*" onChange={(e) => setMediaFile(e.target.files?.[0] || null)} /></Field>

            <div style={s.note}>{runtimeHint}</div>
            {mediaType === "video" ? <Field label="Video Runtime (20-120 sec)"><input style={s.input} type="number" min="20" max="120" value={form.videoDurationSec} onChange={(e) => setForm((p) => ({ ...p, videoDurationSec: e.target.value }))} /></Field> : null}

            {previewUrl ? (mediaType === "video" ? <video src={previewUrl} controls style={s.preview} /> : <img src={previewUrl} alt="preview" style={s.preview} />) : null}
            {isPublishing ? <div style={s.note}>Upload progress: {uploadProgress}%</div> : null}
            {publishError ? <div style={s.error}>{publishError}</div> : null}
            {publishMessage ? <div style={s.ok}>{publishMessage}</div> : null}

            <div style={{ display: "flex", justifyContent: "flex-end" }}><button type="submit" style={s.publishBtn} disabled={isPublishing}>{isPublishing ? "Publishing..." : "Publish Ad"}</button></div>
          </form>
        ) : (
          <section className="aa-pro-table-shell" style={s.panel}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>Manage Ads</h3>
              <label style={s.search}><FaSearch /><input style={s.searchInput} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ads" /></label>
            </div>

            {loadingAds ? <div style={s.note}>Loading ads...</div> : null}
            {!loadingAds && !filteredAds.length ? <div style={s.note}>No ads found.</div> : null}

            <div className="grid3">
              {filteredAds.map((ad) => (
                <article key={ad.id} style={s.card}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><strong>{ad.title || "Untitled Ad"}</strong><span style={chip(ad.status)}>{ad.status || "hold"}</span></div>
                  <small>Scope: {ad.deliveryScope || "all_app"}</small>
                  <small>Pages: {(ad.selectedPages || []).join(", ") || "-"}</small>
                  <small>Slots: {(ad.selectedSlots || []).join(", ") || "-"}</small>
                  <small>Specific: {ad.specificContentId ? `${ad.specificContentType || "content"}:${ad.specificContentId}` : "-"}</small>
                  <small>Duration: {ad.displayDurationSec || "-"} sec</small>
                  {ad.mediaUrl ? (ad.mediaType === "video" ? <video src={ad.mediaUrl} controls style={s.media} /> : <img src={ad.mediaUrl} alt="ad" style={s.media} />) : null}
                  <select style={s.input} value={ad.status || "hold"} onChange={(e) => updateStatus(ad.id, e.target.value)} disabled={updatingAdId === ad.id}>{STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}</select>
                  <small style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>{ad.status === "live" ? <FaToggleOn color="#16a34a" /> : <FaToggleOff color="#f59e0b" />}{ad.status === "live" ? "Live in app" : "Not live"}</small>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </PermissionGuard>
  );
}

function Field({ label, children }) {
  return <label style={{ display: "grid", gap: 6, marginTop: 10 }}><span style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>{label}</span>{children}</label>;
}

function Choice({ title, options, selected, toggle }) {
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 10, background: "#f8fafc", marginTop: 10 }}>
      <div style={{ fontWeight: 800, fontSize: 12, color: "#64748b", marginBottom: 8 }}>{title}</div>
      <div className="checks">{options.map(([v, l]) => <label key={v} style={{ display: "inline-flex", gap: 8, alignItems: "center", border: "1px solid #e2e8f0", padding: "6px 8px", borderRadius: 8, background: "#fff", fontSize: 12 }}><input type="checkbox" checked={selected.includes(v)} onChange={() => toggle(v)} />{l}</label>)}</div>
    </div>
  );
}

function validate(form, mediaFile) {
  if (!form.title.trim()) return "Ad title is required.";
  if (!form.objective.trim()) return "Objective is required.";
  if (!mediaFile) return "Please upload image/video.";
  if (!String(form.ctaUrl || "").trim()) return "CTA URL is required.";
  if (!form.selectedSlots.length) return "Select at least one slot.";
  if (form.deliveryScope === "selected_pages" && !form.selectedPages.length) return "Select at least one page.";
  if (form.deliveryScope === "specific_content" && !String(form.specificContentId || "").trim()) return "Specific content ID is required.";
  if (form.startDate && form.endDate && form.startDate > form.endDate) return "End date must be after start date.";
  if (inferMediaType(mediaFile) === "video") {
    const sec = clampVideoDuration(form.videoDurationSec);
    if (sec < 20 || sec > 120) return "Video runtime must be 20-120 sec.";
  }
  return "";
}

function uploadMedia(file, setProgress) {
  return new Promise((resolve, reject) => {
    const mediaType = inferMediaType(file);
    const ext = String(file.name || "media.bin").split(".").pop();
    const mediaPath = `new_add/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const task = uploadBytesResumable(ref(storage, mediaPath), file, { contentType: file.type || undefined });
    task.on("state_changed", (snap) => {
      const p = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
      setProgress(Number.isFinite(p) ? p : 0);
    }, reject, async () => {
      const mediaUrl = await getDownloadURL(task.snapshot.ref);
      resolve({ mediaUrl, mediaType, mediaPath });
    });
  });
}

function inferMediaType(file) {
  const mime = String(file?.type || "").toLowerCase();
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("image/")) return "image";
  const n = String(file?.name || "").toLowerCase();
  if (/\.(mp4|webm|mov|m4v|ogg)$/.test(n)) return "video";
  return file ? "image" : "";
}

function clampVideoDuration(v) {
  const n = Number(v || 20);
  if (!Number.isFinite(n)) return 20;
  return Math.min(120, Math.max(20, Math.round(n)));
}

function buildAdCompatibilityPatch(ad = {}, status = "hold") {
  const live = String(status || "").toLowerCase() === "live";
  const mediaType = String(ad.mediaType || "").toLowerCase();
  const mediaUrl = String(ad.mediaUrl || ad.imageUrl || ad.videoUrl || ad.url || "");
  const selectedPages = Array.isArray(ad.selectedPages) ? ad.selectedPages : [];
  const selectedSlots = Array.isArray(ad.selectedSlots) ? ad.selectedSlots : [];
  const startDate = ad.startDate || null;
  const endDate = ad.endDate || null;

  return {
    isActive: live,
    active: live,
    isLive: live,
    visible: live,
    enabled: live,
    approvalStatus: live ? "approved" : "pending",
    publishStatus: status,
    statusLabel: status,

    imageUrl: mediaType === "video" ? "" : mediaUrl,
    videoUrl: mediaType === "video" ? mediaUrl : "",
    creativeUrl: mediaUrl,
    url: mediaUrl,
    media_url: mediaUrl,

    pages: selectedPages,
    slots: selectedSlots,
    screenTargets: selectedPages,
    placementSlots: selectedSlots,
    scope: ad.deliveryScope || "all_app",
    placement: selectedSlots[0] || "inline_card",

    startsAt: startDate,
    endsAt: endDate,
    start_at: startDate,
    end_at: endDate,
  };
}

function safeEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function chip(status) {
  const base = { borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 800, textTransform: "capitalize" };
  const v = String(status || "").toLowerCase();
  if (v === "live") return { ...base, background: "#ecfdf5", color: "#047857", border: "1px solid #bbf7d0" };
  if (v === "paused") return { ...base, background: "#fff7ed", color: "#b45309", border: "1px solid #fed7aa" };
  if (v === "completed") return { ...base, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" };
  return { ...base, background: "#fef9c3", color: "#854d0e", border: "1px solid #fde68a" };
}

const css = `
.grid2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
.grid3{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}
.checks{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}
@media (max-width:1100px){.grid3{grid-template-columns:repeat(2,minmax(0,1fr))}.checks{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:760px){.grid2,.grid3,.checks{grid-template-columns:1fr}}
`;

const s = {
  page: { padding: "1rem", fontFamily: "Inter, system-ui, sans-serif", background: "#f5f6f8", minHeight: "100%" },
  hero: { borderRadius: 14, background: "linear-gradient(100deg,#f20c53,#fb6c33)", color: "#fff", padding: 14, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", boxShadow: "0 16px 26px rgba(15,23,42,.17)" },
  heroTitle: { display: "flex", gap: 10, alignItems: "center" },
  tab: { borderRadius: 999, border: "1px solid rgba(255,255,255,.5)", background: "rgba(255,255,255,.16)", color: "#fff", padding: "7px 12px", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: 800 },
  tabActive: { border: "1px solid #fff", background: "#fff", color: "#0f172a" },
  panel: { marginTop: 12, background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 12, boxShadow: "0 12px 20px rgba(15,23,42,.06)" },
  input: { border: "1px solid #e2e8f0", borderRadius: 9, height: 38, padding: "0 10px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" },
  textarea: { border: "1px solid #e2e8f0", borderRadius: 9, padding: 10, minHeight: 90, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" },
  note: { marginTop: 10, border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc", color: "#64748b", padding: "8px 10px", fontSize: 12 },
  error: { marginTop: 10, border: "1px solid #fecaca", borderRadius: 8, background: "#fff1f2", color: "#b91c1c", padding: "8px 10px", fontSize: 13 },
  ok: { marginTop: 10, border: "1px solid #bbf7d0", borderRadius: 8, background: "#ecfdf5", color: "#047857", padding: "8px 10px", fontSize: 13 },
  publishBtn: { border: "none", borderRadius: 10, padding: "10px 14px", color: "#fff", fontWeight: 800, cursor: "pointer", background: "linear-gradient(90deg,#f20c53,#fb6c33)" },
  preview: { width: "min(420px,100%)", maxHeight: 220, borderRadius: 10, border: "1px solid #e2e8f0", objectFit: "cover", marginTop: 10 },
  search: { display: "inline-flex", alignItems: "center", gap: 8, border: "1px solid #e2e8f0", borderRadius: 9, padding: "7px 10px", minWidth: "min(320px,100%)", background: "#fff" },
  searchInput: { border: "none", outline: "none", width: "100%", fontSize: 13 },
  card: { borderRadius: 12, border: "1px solid #e2e8f0", background: "#fff", padding: 10, boxShadow: "0 10px 18px rgba(15,23,42,.06)", display: "grid", gap: 6 },
  media: { width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 8, border: "1px solid #e2e8f0" },
};
